import { cn } from "@/lib/utils";

interface ActivitySparklineProps {
  data: { date: string; count: number }[] | undefined;
  loading?: boolean;
}

/**
 * 30-day uploads-per-day area chart, rendered as a single SVG path.
 * Plays nicely with the dashboard card grid (responsive width, fixed
 * 80px height). Hover bands show the tooltip via the native `title`
 * attr on each per-day rect.
 */
export function ActivitySparkline({ data, loading }: ActivitySparklineProps) {
  const points = data ?? [];
  const total  = points.reduce((s, p) => s + p.count, 0);
  const peak   = points.reduce((m, p) => Math.max(m, p.count), 0);

  // 300x80 viewBox; pad 2px on either side so end caps aren't clipped.
  const W = 300, H = 80, PAD = 2;
  const stepX = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
  const yFor  = (n: number) => (peak > 0 ? H - PAD - (n / peak) * (H - PAD * 2) : H - PAD);

  // Line path
  const linePath = points.length
    ? points.map((p, i) => `${i === 0 ? "M" : "L"} ${PAD + i * stepX} ${yFor(p.count)}`).join(" ")
    : "";
  // Filled-area path (close back along the baseline)
  const areaPath = points.length
    ? `${linePath} L ${PAD + (points.length - 1) * stepX} ${H - PAD} L ${PAD} ${H - PAD} Z`
    : "";

  const fmtDate = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const firstDate = points[0]?.date;
  const lastDate  = points[points.length - 1]?.date;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono text-2xl font-bold tabular-nums tracking-tight">
            {loading ? "—" : total.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            New in last 30 days
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          peak <span className="font-mono tabular-nums text-foreground">{peak}</span>/day
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className={cn("h-20 w-full", loading && "opacity-30")}
        aria-label="Uploads per day, last 30 days"
      >
        {/* Gradient fill */}
        <defs>
          <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.25" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Baseline (1px dashed slate-200ish) */}
        <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD}
              stroke="currentColor" strokeOpacity="0.1" strokeDasharray="2 2" />

        {points.length > 1 && (
          <>
            <path d={areaPath} fill="url(#spark-fill)" />
            <path d={linePath} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.75"
                  strokeLinejoin="round" strokeLinecap="round" />
          </>
        )}

        {/* Invisible per-day rects with native tooltips for hover. */}
        {points.map((p, i) => (
          <rect
            key={p.date}
            x={PAD + i * stepX - stepX / 2}
            y={0}
            width={stepX}
            height={H}
            fill="transparent"
          >
            <title>{`${fmtDate(p.date)} — ${p.count} upload${p.count === 1 ? "" : "s"}`}</title>
          </rect>
        ))}
      </svg>

      {firstDate && lastDate && (
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{fmtDate(firstDate)}</span>
          <span>{fmtDate(lastDate)}</span>
        </div>
      )}
    </div>
  );
}
